import React, { useEffect } from 'react';
import { IStore } from "../../redux/reducers";
import { useSelector } from "react-redux";
import { Table, TableWrapper, Cell } from 'react-native-table-component';
import { TouchableOpacity, Platform } from 'react-native';
import { Text, HStack, View, useColorModeValue, Box } from 'native-base';
import { JobStatus } from '../../models';
import { textStyle } from 'src/styles/textStyle';
import { tableStyle } from 'src/styles/tableStyle';
import { getText } from 'src/utils/i18n';
import { useResponsive } from 'src/hooks/useResponsive';

export default function GrainCumulativeAnalysisTable() {
  const { isDesktop, isLargeScreen } = useResponsive();
  const colorMode = useSelector((store: IStore) => store.settings.colorMode);
  const job = useSelector((store: IStore) => store.job.jobStatus.job);
  const jobStatus = useSelector((store: IStore) => store.job.currentJobStatus);
  const tableHeadBg = useColorModeValue('#f1f8ff', '#2d3748');
  const tableBorderColor = useColorModeValue('#cbd5e1', '#4a5568');
  const titleTextColor = useColorModeValue('coolGray.800', 'coolGray.100');

  const [state, setState] = React.useState<{
    kernelCount: number;
    tableHead: string[];
    tableData: string[][];
  }>({
    kernelCount: 0,
    tableHead: [getText('colName'), getText('colAverage'), getText('colSD')],
    tableData: [
      [getText('grainLength'), '0', '0'],
      [getText('grainWidth'), '0', '0'],
      [getText('grainAspectRatio'), '0', '0'],
      [getText('grainArea'), '0', '0'],
    ]
  });

  const [sortByColumn, setSortByColumn] = React.useState<string | null>(null);
  const [sortOrder, setSortOrder] = React.useState<"asc" | "desc">("asc");

  useEffect(() => {
    if (jobStatus == JobStatus.JOB_COMPLETED && job) {
      const result = [
        [getText('grainLength'), `${beautify(job.results?.avg_length)}`, `${beautify(job.results?.std_length)}`],
        [getText('grainWidth'), `${beautify(job.results?.avg_width)}`, `${beautify(job.results?.std_width)}`],
        [getText('grainAspectRatio'), `${beautify(job.results?.avg_aspect_ratio)}`, `${beautify(job.results?.std_aspect_ratio)}`],
        [getText('grainArea'), `${beautify(job.results?.avg_area_base)}`, `${beautify(job.results?.std_area_base)}`]
      ]

      if(job.grainId === '18') {
        result.push([getText('grainHeadRiceLength'), `${beautify(job.results?.avg_length_of_broken_grains)}`, `${beautify(job.results?.std_length)}`])
      }

      setState((prevState) => ({
        ...prevState,
        tableData: result
      }));
    }
  }, [job, jobStatus]);

  const beautify = (data: any) => {
    if (!isNaN(parseFloat(data)) && !isNaN(data - 0)) {
      let floatData = parseFloat(data);
      if (Math.ceil(floatData) == Math.floor(floatData)) {
        return data;
      } else {
        return floatData.toFixed(2);
      }
    } else {
      return data;
    }
  }

  const sortItems = (column: string) => {
    if (sortByColumn === column) {
      const newSortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
      setSortOrder(newSortOrder);
      applySorting(column, newSortOrder);
    } else {
      setSortByColumn(column);
      setSortOrder("asc");
      applySorting(column, 'asc');
    }
  }

  const applySorting = (column: string, order: "asc" | "desc") => {
    const columnIndex = state.tableHead.indexOf(column);
    if (columnIndex === -1) return;

    const sortedData = [...state.tableData].sort((a: string[], b: string[]) => {
      const aVal = a[columnIndex];
      const bVal = b[columnIndex];

      // Handle numeric values
      const aNum = parseFloat(aVal);
      const bNum = parseFloat(bVal);
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return order === 'asc' ? aNum - bNum : bNum - aNum;
      }

      // Handle string values
      return order === 'asc'
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });

    setState((prevState) => ({
      ...prevState,
      tableData: sortedData
    }));
  }

  const textElement = (data: any) => (
    <Text style={[tableStyle.cellText, {
      color: colorMode === 'dark' ? 'white' : 'black'
    }]}>{data}</Text>
  );

  const headElement = (data: string) => (
    <TouchableOpacity onPress={() => sortItems(data)}>
      <HStack space={1} alignItems="center">
        <Text style={[tableStyle.cellText, {
          color: colorMode === 'dark' ? 'white' : 'black'
        }]}>{data}</Text>
        {sortByColumn === data && (
          <Text style={[tableStyle.cellText, {
            color: colorMode === 'dark' ? 'white' : 'black'
          }]}>{sortOrder === 'asc' ? '↑' : '↓'}</Text>
        )}
      </HStack>
    </TouchableOpacity>
  );


  return (
    <Box mb={isLargeScreen ? 6 : 3}>
      <Text color={titleTextColor} style={textStyle.tableTitleText}>{getText('cumulativeAnalysis')}</Text>
      <View style={isLargeScreen ? { paddingHorizontal: 12 } : {}}>
        <Table borderStyle={{ borderWidth: 1, borderColor: tableBorderColor }}>
          <TableWrapper style={[tableStyle.head, { backgroundColor: tableHeadBg }]}>
            {
              state.tableHead.map((cellData, index) => (
                <Cell key={index} data={headElement(cellData)} />
              )) as any
            }
          </TableWrapper>
          {
            state.tableData.map((rowData, index) => (
              <TableWrapper key={index} style={tableStyle.row}>
                {
                  rowData.map((cellData, cellIndex) => (
                    <Cell key={cellIndex} data={textElement(beautify(cellData))} />
                  )) as any
                }
              </TableWrapper>
            )) as any
          }
        </Table>
      </View>
    </Box>
  );
};